import { Text, TextInput, TokenTextAppearance, TokenTextColor } from '@volkswagen-onehub/components-core';
import React, { ReactElement, ReactNode, useLayoutEffect, useRef, useState } from "react";
import { useController, useFormContext } from 'react-hook-form';
import styled from 'styled-components';

const ErrorBelow = styled.div`
  margin-top: ${(props) => props.theme.size.static100};
  color: #e4002c;
`;

export interface TextInputFieldRules {
  required?: boolean;
  max?: string | number;
  min?: string | number;
  maxLength?: number;
}

export interface TextInputFieldProps {
  name: string;
  label: string;
  type?: 'text' | 'email' | 'tel' | 'search' | 'url' | 'none' | 'numeric' | 'decimal';
  placeholder?: string;
  rules?: TextInputFieldRules;
  hasClearButton?: boolean;
  isFloating?: boolean;
  appearance?: 'default' | 'information' | 'disabled' | 'error' | 'success' | 'readonly' | 'validation';
  trimString?: boolean;
  icon?: ReactNode;
  autoFocus?: boolean;
  hideAsterisk?: boolean;
  // Shows the error as a line under the field the whole time it applies, instead of VW's
  // notification that only appears while the field is focused and pushes the page down.
  errorBelow?: boolean;

  onClear?(): void;

  // Drops characters the field does not accept while they are being typed.
  sanitize?(value: string): string;

  onChange?(value: string): void;

  onFocus?(): void;
}

export function TextInputField({
  name,
  type,
  trimString,
  sanitize,
  rules,
  onChange,
  appearance,
  onClear,
  onFocus,
  hideAsterisk,
  errorBelow,
  ...rest
}: TextInputFieldProps): ReactElement {
  const { control } = useFormContext();

  const {
    field: { onChange: formOnChange, onBlur, value, ref },
    fieldState: { invalid, error },
    formState: { isSubmitted },
  } = useController({
    name,
    control,
  });

  const textInputRef = useRef<HTMLDivElement>(null);

  const [originalMaskedValue, setOriginalMaskedValue] = useState<string | null>(null);
  const [isCleared, setIsCleared] = useState(false);

  useLayoutEffect(() => {
    if (!hideAsterisk) return;
    if (!textInputRef.current) return;

    const p = textInputRef.current.querySelector('label p');
    if (p && p.textContent) {
      p.textContent = p.textContent.replace(/\s*\*$/, '');
    }
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onChangeWrapper = (...events: any[]): void => {
    if (trimString) {
      events[0].target.value = events[0].target.value.trim();
    }

    if (sanitize) {
      events[0].target.value = sanitize(events[0].target.value);
    }

    formOnChange(...events);

    if (onChange) {
      onChange(events[0].target.value);
    }
  };

  const handleFocus = (): void => {
    if (typeof value === 'string' && value.includes('*')) {
      setOriginalMaskedValue(value);
      formOnChange('');
      setIsCleared(true);
    }

    if (onFocus) {
      onFocus();
    }
  };

  const handleBlur = (): void => {
    if (isCleared && (!value || value.trim() === '')) {
      if (originalMaskedValue) {
        formOnChange(originalMaskedValue);
      }
    }

    setIsCleared(false);
    setOriginalMaskedValue(null);
    onBlur();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  const showError = isSubmitted && invalid;
  const errorMessage = showError ? error?.message : undefined;
  const appearanceError = showError ? 'error' : undefined;

  return (
    <div ref={textInputRef}>
      <TextInput
        value={value}
        ref={ref}
        onBlur={handleBlur}
        onFocus={handleFocus}
        message={errorBelow ? undefined : errorMessage}
        onChange={onChangeWrapper}
        isClearButtonVisible={!!value}
        inputMode={type}
        onClear={onClear}
        appearance={appearance ?? appearanceError}
        onKeyDown={handleKeyDown}
        {...rules}
        {...rest}
      />
      {errorBelow && errorMessage && (
        <ErrorBelow role="alert">
          <Text appearance={TokenTextAppearance.copy100} color={TokenTextColor.inherit}>{errorMessage}</Text>
        </ErrorBelow>
      )}
    </div>
  );
}
